import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import dayjs, { Dayjs } from "dayjs";

import FormHelperText from "@mui/material/FormHelperText";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { ClockTime, FormDateTimeInput, TimeOffering } from "stentor-models";

import { isBusyDay, parseBusyDays } from "../../utils/busyDays";
import {
    firstSelectableDate,
    formatOfferingLabel,
    formatTimezoneLabel,
    generateOfferings,
    horizonDaysOf,
    hostTimezone,
    isDateSelectable,
    isUsableTimezone,
    toCalendarDate,
    toOffsetIso,
} from "../../utils/timeSlots";

import { FormFieldProps } from "../FormDisplay";

import styles from "./FormFieldDateTime.module.scss";

/**
 * What the field reports through `onFieldChange`, and what it accepts back to resume.
 *
 * Everything a lead consumer needs is resolved here rather than at submit time, because
 * this is where the schedule - and so the timezone the times are expressed in - is known.
 */
export interface FormDateTimeValue {
    /**
     * The chosen day as the business sees it, "YYYY-MM-DD". Never shifted into UTC.
     */
    date: string;
    /**
     * What the chosen time reads as, and what is submitted for the field itself.
     */
    label: string;
    /**
     * When the appointment starts, "HH:MM".
     */
    start: ClockTime;
    /**
     * When it ends, "HH:MM". Absent for a discrete start time.
     */
    end?: ClockTime;
    /**
     * Date and time together as ISO 8601 carrying the schedule's offset on that date,
     * e.g. "2026-08-24T09:30:00-04:00", so the value survives a DST transition.
     */
    datetime: string;
}

export const NO_DATE_MESSAGE = "Select a date to see the available times.";
export const NO_TIMES_MESSAGE = "No times are available on this date.";
export const TIMES_LABEL = "Appointment times";
export const TIMEZONE_NOTICE_PREFIX = "Times shown in";

export interface FormDateTimeProps extends FormFieldProps {
    readonly field: FormDateTimeInput;
    /**
     * A previously chosen date and time, so stepping back into the form resumes it.
     */
    readonly value?: FormDateTimeValue;
    /**
     * The current moment. Injectable so tests and Storybook get a fixed calendar rather
     * than whatever day they happen to run on.
     */
    readonly now?: Date;
    /**
     * The days the tenant's CRM has declared full, comma delimited - "YYYY-MM-DD,YYYY-MM-DD".
     *
     * Arrives on a channel action response and so is absent on the first render; until it
     * turns up the calendar shows the schedule's own availability. Busy days narrow that
     * availability, they never widen it.
     */
    readonly busyDays?: string;
    /**
     * Open on the soonest date that actually has times, instead of on the prompt.
     *
     * For a preview that lands straight on this step: a calendar with an empty
     * column beside it reads as a form with nothing to offer, when the schedule
     * is in fact full of slots one click away.
     *
     * Off by default, and deliberately not derived from `demoMode`. On a real
     * booking page a preselected date is a date the customer never chose, and
     * nothing stops them carrying it through to submit -- an appointment request
     * for a day picked by the software. Only the date is chosen; the time is
     * still theirs.
     */
    readonly preselectFirstAvailable?: boolean;
}

/**
 * Identifies an offering within a day's column. Start and end together, rather than the
 * index, so a selection survives a re-render that regenerates the array.
 */
function offeringKey(offering: TimeOffering): string {
    return `${offering.start}|${offering.end || ""}`;
}

/**
 * Whether two selections name the same appointment.
 *
 * Compared field by field rather than by reference, so a value rebuilt from the same date
 * and offering counts as unchanged.
 */
function sameSelection(a: FormDateTimeValue, b: FormDateTimeValue): boolean {
    return (
        a.date === b.date &&
        a.start === b.start &&
        a.end === b.end &&
        a.label === b.label &&
        a.datetime === b.datetime
    );
}

/**
 * What the column says the times are expressed in.
 *
 * A visitor two zones away reads "9:30 AM" as their own 9:30 unless something says
 * otherwise, so the zone is named outright - and named again as theirs when the two
 * differ, which is the only case where the distinction costs them an appointment. Zones
 * that merely have different identifiers but read the same, "America/Detroit" against
 * "America/New_York", are not a difference worth raising.
 *
 * @param timezone        The zone the times are actually in.
 * @param visitorTimezone The zone the visitor's browser reports, if any.
 */
function timezoneNotice(timezone: string, visitorTimezone: string): string {
    const name = formatTimezoneLabel(timezone);

    if (!name) {
        return "";
    }

    const visitorName =
        visitorTimezone && visitorTimezone !== timezone
            ? formatTimezoneLabel(visitorTimezone)
            : "";

    if (visitorName && visitorName !== name) {
        return `${TIMEZONE_NOTICE_PREFIX} ${name}, not your time zone (${visitorName}).`;
    }

    return `${TIMEZONE_NOTICE_PREFIX} ${name}.`;
}

/**
 * Calendar beside a scrollable column of appointment times, in the manner of Calendly.
 *
 * The column is generated from `field.schedule` on the client, so picking a date repaints
 * it immediately - there is no availability lookup and no round trip to wait on. Changing
 * the date clears the chosen time, because a time that exists on one day frequently does
 * not exist on the next.
 */
export const FormFieldDateTime: FC<FormDateTimeProps> = (props) => {
    const { field, onFieldChange, errorMessage, value, required, busyDays, preselectFirstAvailable } =
        props;

    const schedule = field.schedule;

    // A fresh `new Date()` on every render would churn every memo below it.
    const now = useMemo(() => props.now || new Date(), [props.now]);

    const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>(() =>
        value?.date ? dayjs(`${value.date}T00:00:00`) : undefined
    );
    const [selectedKey, setSelectedKey] = useState<string | undefined>(() =>
        value ? offeringKey(value) : undefined
    );

    const offerings = useMemo(
        () => (selectedDate ? generateOfferings(schedule, selectedDate.toDate(), now) : []),
        [schedule, selectedDate, now]
    );

    const selected = useMemo(
        () => offerings.find((offering) => offeringKey(offering) === selectedKey),
        [offerings, selectedKey]
    );

    const fieldValue = useMemo<FormDateTimeValue | undefined>(() => {
        if (!selectedDate || !selected) {
            return undefined;
        }

        const date = toCalendarDate(selectedDate.toDate());

        const next: FormDateTimeValue = {
            date,
            label: formatOfferingLabel(selected),
            start: selected.start,
            datetime: toOffsetIso(date, selected.start, schedule?.timezone),
        };

        if (selected.end) {
            next.end = selected.end;
        }

        // Hand back the very object the step passed down when the selection has not
        // actually moved off it. The step's prefill guard only releases on reference
        // equality, so reporting a freshly built twin reads as a stale update: the guard
        // stays closed and every later edit is silently discarded, leaving the submitted
        // value frozen at the prefill while the UI shows the new choice.
        if (value && sameSelection(value, next)) {
            return value;
        }

        return next;
    }, [selectedDate, selected, schedule, value]);

    // fieldValue is memoised, so this only reaches the step when the selection really
    // changed - not every time the parent hands down a new onFieldChange.
    useEffect(() => {
        onFieldChange(field.name, fieldValue);
    }, [fieldValue, field.name, onFieldChange]);

    // Memoised on the string itself so a busyDays that arrives later - or changes -
    // gives shouldDisableDate a new identity and the calendar repaints.
    const disabledDays = useMemo(() => parseBusyDays(busyDays), [busyDays]);

    // A date chosen before the CRM answered can turn out to be full. The selection is
    // dropped entirely rather than kept and greyed: the lead is asked again instead of
    // submitting against a day with no capacity, and the field reports undefined so a
    // mandatory step will not advance on it.
    useEffect(() => {
        if (selectedDate && isBusyDay(selectedDate, disabledDays)) {
            setSelectedDate(undefined);
            setSelectedKey(undefined);
        }
    }, [selectedDate, disabledDays]);

    // The soonest day with times, for a caller that asked to open on one.
    //
    // Recomputed against `disabledDays`, so this also answers the case the effect
    // above creates: busy days arrive after the first render, and a preselected
    // day that turns out to be full is cleared there and re-picked here as the
    // next day that is not. The search excludes busy days, so the day it lands on
    // cannot be cleared again and the two effects settle rather than trade.
    const preselected = useMemo(
        () =>
            preselectFirstAvailable
                ? firstSelectableDate(schedule, now, (date) =>
                      isBusyDay(dayjs(date), disabledDays)
                  )
                : undefined,
        [preselectFirstAvailable, schedule, now, disabledDays]
    );

    // Only ever fills an empty selection: a resumed value, or a day they picked
    // themselves, is theirs. And only the date -- the time is the answer, and
    // answering for someone is how a preview stops being a preview of their form.
    useEffect(() => {
        if (!preselected || selectedDate) {
            return;
        }

        setSelectedDate(dayjs(preselected));
    }, [preselected, selectedDate]);

    const onDateChange = (next: Dayjs) => {
        setSelectedDate(next);
        // A 9:30 slot valid on Tuesday may not exist on Saturday, so the time goes.
        setSelectedKey(undefined);
    };

    const shouldDisableDate = useCallback(
        (day: Dayjs): boolean =>
            !isDateSelectable(schedule, day.toDate(), now) || isBusyDay(day, disabledDays),
        [schedule, now, disabledDays]
    );

    const horizon = horizonDaysOf(schedule);

    // What the times are *actually* in. An unusable zone is substituted for the host's
    // deep inside timeSlots rather than thrown, so the notice has to follow that
    // substitution or it would name a zone the column is not using.
    const hasSchedule = !!schedule;
    const scheduleTimezone = schedule?.timezone;
    const timezoneUsable = isUsableTimezone(scheduleTimezone);
    const notice = useMemo(() => {
        if (!hasSchedule) {
            return "";
        }

        const visitorTimezone = hostTimezone();

        return timezoneNotice(
            timezoneUsable ? scheduleTimezone : visitorTimezone,
            visitorTimezone
        );
    }, [hasSchedule, scheduleTimezone, timezoneUsable]);

    // The substitution is otherwise silent, and a typo'd zone renders times that are
    // wrong for everybody. Once per schedule, not once per render: a warning that
    // repeats on every click is one a developer learns to scroll past.
    // Keyed on the values rather than on the schedule object, so a parent that rebuilds
    // its field on every render does not turn this into a warning per keystroke.
    useEffect(() => {
        if (hasSchedule && !timezoneUsable) {
            console.warn(
                `FormFieldDateTime: "${scheduleTimezone}" is not a usable timezone, so ` +
                    `times are shown in the visitor's zone instead. Set the schedule's ` +
                    `timezone to an IANA identifier such as "America/New_York".`
            );
        }
    }, [hasSchedule, scheduleTimezone, timezoneUsable]);

    // Rendered as one flat list rather than nested lists: a group is a heading in the
    // middle of the column, so a schedule that names only some of its offerings still
    // reads as a single scrollable run.
    let previousGroup: string | undefined;

    return (
        <div className={styles.xapp_datetime} style={field.style}>
            {!!field.title && <h2 className={styles.xapp_datetime_title}>{field.title}</h2>}

            <div className={styles.xapp_datetime_body}>
                <div className={styles.xapp_datetime_calendar}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar
                            // No disablePast: MUI measures that against the real system
                            // clock, which would overrule the now this component was
                            // handed. Past dates are already unselectable through
                            // shouldDisableDate, which does read that now.
                            maxDate={dayjs(now).add(horizon, "day")}
                            onChange={onDateChange}
                            referenceDate={dayjs(now)}
                            shouldDisableDate={shouldDisableDate}
                            value={selectedDate || null}
                            views={["day"]}
                        />
                    </LocalizationProvider>
                </div>

                <div className={styles.xapp_datetime_times_column}>
                    {!!notice && (
                        // Deliberately a sibling of the group rather than a child: the
                        // group's accessible name already carries the requirement, and a
                        // screen reader reads this on its way past either way.
                        <p className={styles.xapp_datetime_timezone}>{notice}</p>
                    )}

                    <div
                        // `aria-required` is not valid on role="group", and the offerings
                        // are buttons rather than form controls, so the requirement rides
                        // in the accessible name where a screen reader will actually
                        // announce it.
                        aria-label={required ? `${TIMES_LABEL} (required)` : TIMES_LABEL}
                        className={styles.xapp_datetime_times}
                        role="group"
                    >
                        {!selectedDate && (
                            <p className={styles.xapp_datetime_message}>{NO_DATE_MESSAGE}</p>
                        )}

                        {!!selectedDate && offerings.length === 0 && (
                            <p className={styles.xapp_datetime_message}>{NO_TIMES_MESSAGE}</p>
                        )}

                        {offerings.map((offering, index) => {
                            const key = offeringKey(offering);
                            const header =
                                offering.group !== previousGroup ? offering.group : undefined;
                            previousGroup = offering.group;

                            return (
                                <React.Fragment key={`${key}-${index}`}>
                                    {!!header && (
                                        <h3 className={styles.xapp_datetime_group}>{header}</h3>
                                    )}
                                    <button
                                        aria-pressed={key === selectedKey}
                                        className={`${styles.xapp_datetime_time} ${
                                            key === selectedKey
                                                ? styles.xapp_datetime_time_selected
                                                : ""
                                        }`}
                                        onClick={() => setSelectedKey(key)}
                                        type="button"
                                    >
                                        {formatOfferingLabel(offering)}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            {!!errorMessage && (
                <FormHelperText className={styles.xapp_datetime_error} error>
                    {errorMessage}
                </FormHelperText>
            )}
        </div>
    );
};

export default FormFieldDateTime;
